import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../lib/api';

import { resolveImageUrl } from '../../lib/utils';

const AboutPage = () => {
  const [homeSettings, setHomeSettings] = useState<any>(null);
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [about, team] = await Promise.all([
        apiClient.getAboutSettings(),
        apiClient.getTeamMembers()
      ]);

      // Ensure all required fields are present with defaults
      const defaultSettings = {
        hero_title: 'About Our Story',
        hero_subtitle: "Discover the journey behind India's premier men's grooming destination",
        story_title: 'Crafting Excellence Since 2014',
        story_content: 'Fully A/C Salon began with a simple vision: to provide men with the highest quality grooming services in a luxurious, comfortable environment.',
        mission_title: 'Our Mission',
        mission_content: "To provide exceptional men's grooming services in a luxurious, fully air-conditioned environment.",
        vision_title: 'Our Vision',
        vision_content: "To become India's most trusted and recognized premium men's grooming brand.",
        stats_clients: 5000,
        stats_experience: 10,
        stats_locations: 3,
        stats_services: 27,
        whatsapp_number: '917484984048',
        hero_image_url: '',
        story_image_url: ''
      };

      setHomeSettings({ ...defaultSettings, ...about });
      setTeamMembers(team || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      // Set fallback data
      setHomeSettings({
        hero_title: 'About Our Story',
        hero_subtitle: "Discover the journey behind India's premier men's grooming destination",
        story_title: 'Crafting Excellence Since 2014',
        story_content: 'Fully A/C Salon began with a simple vision: to provide men with the highest quality grooming services in a luxurious, comfortable environment.',
        mission_title: 'Our Mission',
        mission_content: "To provide exceptional men's grooming services in a luxurious, fully air-conditioned environment.",
        vision_title: 'Our Vision',
        vision_content: "To become India's most trusted and recognized premium men's grooming brand.",
        stats_clients: 5000,
        stats_experience: 10,
        stats_locations: 3,
        stats_services: 27,
        whatsapp_number: '917484984048',
        hero_image_url: '',
        story_image_url: ''
      });
      setTeamMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const whatsappBooking = () => {
    const message = "Hi! I would like to know more about your services and book an appointment.";
    const phone = homeSettings?.whatsapp_number || '';
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const stats = [
    { number: `${homeSettings?.stats_clients || 5000}+`, label: 'Happy Clients' },
    { number: `${homeSettings?.stats_experience || 10}+`, label: 'Years Experience' },
    { number: `${homeSettings?.stats_locations || 3}`, label: 'Locations' },
    { number: `${homeSettings?.stats_services || 27}`, label: 'Premium Services' }
  ];


  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative py-32 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('${resolveImageUrl(homeSettings?.hero_image_url) || ''}')`
        }}
      >
        <div className="container text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 text-shadow-luxury animate-fadeInUp">
            {homeSettings?.hero_title || "About Our Story"}
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            {homeSettings?.hero_subtitle || "Discover the journey behind India's premier men's grooming destination"}
          </p>
        </div>
      </section>

      {/* Brand Story Section */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fadeInLeft">
              <h2 className="text-primary-400 text-lg font-semibold mb-4 tracking-wide uppercase">
                Our Story
              </h2>
              <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                {homeSettings?.story_title || "Crafting Excellence Since 2014"}
              </h3>
              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                {homeSettings?.story_content || "Fully A/C Salon began with a simple vision: to provide men with the highest quality grooming services in a luxurious, comfortable environment. What started as a single location has grown into a premium brand trusted by thousands."}
              </p>
              <button
                onClick={whatsappBooking}
                className="bg-primary-400 hover:bg-primary-500 text-black px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 btn-hover-effect whitespace-nowrap cursor-pointer"
              >
                Experience Our Service
              </button>
            </div>
            <div className="animate-fadeInRight">
              <img
                src={resolveImageUrl(homeSettings?.story_image_url) || ''}
                alt="Our Story"
                className="w-full h-96 object-cover rounded-2xl shadow-luxury"
                onError={(e) => {
                  console.error('About page story image failed to load:', homeSettings?.story_image_url);
                }}
                onLoad={() => {
                  console.log('About page story image loaded successfully:', homeSettings?.story_image_url);
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 luxury-gradient">
        <div className="container">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className={`text-center animate-fadeInUp`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="text-4xl md:text-5xl font-bold text-primary-400 mb-2">
                  {stat.number}
                </div>
                <div className="text-white font-semibold">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20 bg-gray-50">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-primary-400 text-lg font-semibold mb-4 tracking-wide uppercase">
              Our Purpose
            </h2>
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Mission & Vision
            </h3>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="glass-effect bg-white rounded-2xl p-8 animate-fadeInLeft">
              <div className="w-16 h-16 bg-primary-400 rounded-full flex items-center justify-center mb-6">
                <i className="ri-target-line text-2xl text-black"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">{homeSettings?.mission_title || "Our Mission"}</h4>
              <p className="text-lg text-gray-600 leading-relaxed">
                {homeSettings?.mission_content || "To provide exceptional men's grooming services in a luxurious, fully air-conditioned environment. We strive to exceed expectations through skilled craftsmanship, premium products, and personalized attention to every client's unique style and preferences."}
              </p>
            </div>

            <div className="glass-effect bg-white rounded-2xl p-8 animate-fadeInRight">
              <div className="w-16 h-16 bg-primary-400 rounded-full flex items-center justify-center mb-6">
                <i className="ri-eye-line text-2xl text-black"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">{homeSettings?.vision_title || "Our Vision"}</h4>
              <p className="text-lg text-gray-600 leading-relaxed">
                {homeSettings?.vision_content || "To become India's most trusted and recognized premium men's grooming brand. We envision expanding our services nationwide while maintaining the highest standards of quality, innovation, and customer satisfaction that define the Fully A/C Salon experience."}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-primary-400 text-lg font-semibold mb-4 tracking-wide uppercase">
              Meet Our Team
            </h2>
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Expert Professionals
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our skilled team of grooming specialists brings years of experience and passion to every service.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <div
                key={index}
                className={`bg-gray-50 rounded-2xl p-6 text-center shadow-lg hover:shadow-glow transition-all duration-500 animate-fadeInUp`}
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <img
                  src={resolveImageUrl(member.image_url)}
                  alt={member.name}
                  className="w-48 h-64 object-cover rounded-xl mx-auto mb-4"
                />
                <h4 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h4>
                <p className="text-primary-400 font-semibold mb-1">{member.position}</p>
                <p className="text-gray-600">{member.experience}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 gold-gradient">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-black text-lg font-semibold mb-4 tracking-wide uppercase">
              Our Values
            </h2>
            <h3 className="text-4xl md:text-5xl font-bold text-black mb-6">
              What Sets Us Apart
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center animate-fadeInUp">
              <div className="w-20 h-20 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-award-line text-3xl text-primary-400"></i>
              </div>
              <h4 className="text-xl font-bold text-black mb-3">Excellence</h4>
              <p className="text-gray-800 leading-relaxed">
                We maintain the highest standards in every service, ensuring exceptional results that exceed expectations.
              </p>
            </div>

            <div className="text-center animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
              <div className="w-20 h-20 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-snowflake-line text-3xl text-primary-400"></i>
              </div>
              <h4 className="text-xl font-bold text-black mb-3">Comfort</h4>
              <p className="text-gray-800 leading-relaxed">
                Our fully air-conditioned environment provides the ultimate comfort for a relaxing grooming experience.
              </p>
            </div>

            <div className="text-center animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
              <div className="w-20 h-20 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-heart-line text-3xl text-primary-400"></i>
              </div>
              <h4 className="text-xl font-bold text-black mb-3">Personal Care</h4>
              <p className="text-gray-800 leading-relaxed">
                Every client receives personalized attention and customized services tailored to their unique needs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 luxury-gradient">
        <div className="container text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Experience the Difference?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Join thousands of satisfied clients who trust us with their grooming needs.
              Book your appointment today and discover why we're the premier choice for men's styling.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/booking"
                className="bg-primary-400 hover:bg-primary-500 text-black px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 btn-hover-effect whitespace-nowrap cursor-pointer inline-block"
              >
                Book Now
              </Link>
              <Link
                to="/services"
                className="border-2 border-primary-400 text-primary-400 hover:bg-primary-400 hover:text-black px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 whitespace-nowrap cursor-pointer inline-block"
              >
                View Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
